import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Alert,
  Chip,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TopBar from './TopBar';
import { RoundedCard } from './shared/StyledComponents';
import { getToken, logout } from '../lib/auth';
import { isWithinUploadLimit, tooLargeMessage } from '../lib/uploadLimits';

// Fallback list, used only if the shared admin-managed list can't be fetched.
const BUSINESS_CATEGORIES = [
  'Cafés', 'Restaurants', 'Hair Salons', 'Nail Salons', 'Gyms',
  'Car Washes', 'Pet Groomers', 'Massage Studios', 'Beauty Bars',
  'Bakeries', 'Juice Bars', 'Barbershops', 'Other',
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
}));

const SectionCard = styled(RoundedCard)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #e8e8e8',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeIn} 0.4s ease-out`,
  padding: theme.spacing(3),
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
  },
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.2s ease',
    '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' },
    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(255, 183, 27, 0.15)' },
  },
}));

const SaveButton = styled(Button)(() => ({
  borderRadius: 14,
  textTransform: 'none',
  fontWeight: 700,
  padding: '14px 32px',
  fontSize: '0.95rem',
  background: 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)',
  color: '#000',
  boxShadow: '0 4px 16px rgba(255, 183, 27, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)',
    boxShadow: '0 6px 24px rgba(255, 183, 27, 0.4)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
}));

const IconCircle = styled(Box)(({ $bgColor = '#fff8e7' }) => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  backgroundColor: $bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

export default function StoreSettings() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const token = getToken();

  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  // Account cancellation
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelledInfo, setCancelledInfo] = useState('');

  // Profile fields
  const [logo, setLogo] = useState(null);
  const [cover, setCover] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [address, setAddress] = useState('');
  const [hours, setHours] = useState('');
  const [website, setWebsite] = useState('');
  const [category, setCategory] = useState('');
  const [categoryOptions, setCategoryOptions] = useState(BUSINESS_CATEGORIES);

  // Brand story
  const [brandMeaning, setBrandMeaning] = useState('');
  const [customerFeeling, setCustomerFeeling] = useState('');
  const [customerSaying, setCustomerSaying] = useState('');
  const [customerReason, setCustomerReason] = useState('');

  // Menu & items
  const [items, setItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', price: '' });
  const [savingItem, setSavingItem] = useState(false);
  const [itemError, setItemError] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [menuUploading, setMenuUploading] = useState(false);
  const [menuScanMessage, setMenuScanMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/vendor/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load vendor data');
        const data = await res.json();
        if (data.vendor) {
          setBusinessName(data.vendor.company || '');
          setAddress(data.vendor.address || '');
          setHours(data.vendor.open_hours || '');
          setWebsite(data.vendor.website || '');
          setCategory(data.vendor.category || '');
          setBrandMeaning(data.vendor.brand_meaning || '');
          setCustomerFeeling(data.vendor.customer_feeling || '');
          setCustomerSaying(data.vendor.customer_saying || '');
          setCustomerReason(data.vendor.customer_reason || '');
          if (data.vendor.logo) setLogo(data.vendor.logo);
          if (data.vendor.banner) setCover(data.vendor.banner);
        }
        if (data.items) setItems(data.items);
      } catch (err) {
        console.error('Error fetching vendor:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, token]);

  useEffect(() => {
    fetch('/api/business-categories')
      .then((r) => r.json())
      .then((data) => {
        if (data && Array.isArray(data.categories) && data.categories.length > 0) {
          setCategoryOptions(data.categories);
        }
      })
      .catch(() => {});
  }, []);

  const logoPreview = logo instanceof File ? URL.createObjectURL(logo) : typeof logo === 'string' ? logo : null;
  const coverPreview = cover instanceof File ? URL.createObjectURL(cover) : typeof cover === 'string' ? cover : null;

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('company', businessName);
      formData.append('address', address);
      formData.append('open_hours', hours);
      formData.append('website', website);
      formData.append('category', category);
      formData.append('brand_meaning', brandMeaning);
      formData.append('customer_feeling', customerFeeling);
      formData.append('customer_saying', customerSaying);
      formData.append('customer_reason', customerReason);
      if (logo instanceof File) formData.append('logo', logo);
      if (cover instanceof File) formData.append('cover', cover);

      const res = await fetch(`/api/vendor/${userId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Failed to save');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleMenuUpload = async (e) => {
    const input = e.target;
    const file = input.files[0];
    if (!file) return;

    // Client-side guards for instant feedback (the server enforces these too).
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      setMenuScanMessage('Please upload an image or a PDF menu.');
      input.value = '';
      return;
    }
    if (!isWithinUploadLimit(file)) {
      setMenuScanMessage(tooLargeMessage());
      input.value = '';
      return;
    }

    setMenuUploading(true);
    setMenuScanMessage('');
    try {
      const formData = new FormData();
      formData.append('menu', file);
      const res = await fetch(`/api/vendor/${userId}/upload-menu`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to scan menu.');
      const newItems = data.newItems;
      if (Array.isArray(newItems) && newItems.length > 0) {
        setItems((prev) => [...prev, ...newItems]);
        setMenuScanMessage(`Added ${newItems.length} new item${newItems.length !== 1 ? 's' : ''} from your menu.`);
      } else {
        setMenuScanMessage('No new items found in this menu.');
      }
    } catch (err) {
      setMenuScanMessage(err.message || 'Failed to scan menu.');
    } finally {
      setMenuUploading(false);
      input.value = ''; // allow re-selecting the same file
    }
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) return;
    if (newItemPrice && isNaN(Number(newItemPrice))) return;

    const name = newItemName.trim();
    const price = newItemPrice;
    setItemError('');

    try {
      const res = await fetch(`/api/vendor/${userId}/add-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ itemName: name, itemPrice: price }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || 'Could not add the item.');

      // Append the saved document, so the row carries the real _id that edit
      // and delete depend on.
      setItems((prev) => [...prev, data.item]);
      setNewItemName('');
      setNewItemPrice('');
    } catch (err) {
      setItemError(err.message || 'Could not add the item.');
    }
  };

  // Inline editing of a menu item. editingId is the item being edited; the
  // draft holds the in-progress values so cancelling restores cleanly.
  const startEditItem = (item) => {
    setEditingItemId(item._id);
    setEditDraft({ name: item.name || '', price: item.price ?? '' });
    setItemError('');
  };

  const cancelEditItem = () => {
    setEditingItemId(null);
    setEditDraft({ name: '', price: '' });
    setItemError('');
  };

  const saveEditItem = async () => {
    const name = editDraft.name.trim();
    if (!name) { setItemError('Item name is required.'); return; }
    if (editDraft.price !== '' && !(Number(editDraft.price) >= 0)) {
      setItemError('Price must be a positive number.');
      return;
    }

    setSavingItem(true);
    setItemError('');
    try {
      const res = await fetch(`/api/vendor/${userId}/update-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ itemId: editingItemId, itemName: name, itemPrice: editDraft.price }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || 'Could not save the item.');

      setItems((prev) => prev.map((it) => (it._id === editingItemId ? data.item : it)));
      cancelEditItem();
    } catch (err) {
      setItemError(err.message || 'Could not save the item.');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (index) => {
    const itemToDelete = items[index];
    if (editingItemId === itemToDelete._id) cancelEditItem();
    const confirmed = window.confirm(`Are you sure you want to delete "${itemToDelete.name}"?`);
    if (!confirmed) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
    try {
      await fetch(`/api/vendor/${userId}/delete-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ itemId: itemToDelete._id }),
      });
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleCancelAccount = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      const res = await fetch(`/api/vendor/${userId}/cancel-account`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to cancel account');

      if (data.cancelAtPeriodEnd) {
        // Subscription cancelled at period end — the vendor keeps access until
        // the end of the month they've already paid for, so don't sign them out.
        const end = data.currentPeriodEnd
          ? new Date(data.currentPeriodEnd).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
          : null;
        setCancelledInfo(end
          ? `Your membership is scheduled to end on ${end}. You'll keep full access until then.`
          : 'Your membership will end at the close of your current billing period. You keep access until then.');
        setCancelling(false);
      } else {
        // Immediate deactivation (no active subscription to run out).
        logout();
        navigate('/login', { replace: true });
      }
    } catch (err) {
      console.error('Error cancelling account:', err);
      setCancelError(err.message || 'Something went wrong. Please try again.');
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <TopBar />
        <ContentContainer maxWidth="xl">
          <Typography>Loading...</Typography>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: '#111', mb: 0.5 }}>
            Store Settings
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Manage your store profile, branding, and menu items
          </Typography>
        </Box>

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSaveSuccess(false)}>
            Store settings saved successfully!
          </Alert>
        )}

        {/* Row 1: Business Info + Preview */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>
          {/* Business Information */}
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <IconCircle $bgColor="#fff3e0">
                <StorefrontIcon sx={{ color: '#ff9800' }} />
              </IconCircle>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Business Information</Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>Basic details about your store</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <StyledTextField label="Business Name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} fullWidth />
              <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2.5 }}>
                <StyledTextField label="Store Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
                <StyledTextField label="Opening Hours" value={hours} onChange={(e) => setHours(e.target.value)} fullWidth />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2.5 }}>
                <StyledTextField label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} fullWidth />
                <StyledTextField label="Category" select value={category} onChange={(e) => setCategory(e.target.value)} fullWidth>
                  <MenuItem value="">Select a category</MenuItem>
                  {(category && !categoryOptions.includes(category)
                    ? [category, ...categoryOptions]
                    : categoryOptions
                  ).map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </StyledTextField>
              </Box>
            </Box>
          </SectionCard>

          {/* Store Preview */}
          <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3, pb: 2 }}>
              <IconCircle $bgColor="#e3f2fd">
                <VisibilityIcon sx={{ color: '#1976d2' }} />
              </IconCircle>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Store Preview</Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>How customers see your profile</Typography>
              </Box>
            </Box>
            {coverPreview ? (
              <Box component="img" src={coverPreview} alt="Cover" sx={{ width: '100%', height: 160, objectFit: 'cover' }} />
            ) : (
              <Box sx={{ width: '100%', height: 160, bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">No cover image</Typography>
              </Box>
            )}
            <Box sx={{ px: 3, pb: 3, mt: -4 }}>
              <Avatar src={logoPreview} alt={businessName} sx={{ width: 72, height: 72, border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
                {businessName || 'Business Name'}
              </Typography>
              {category && <Chip label={category} size="small" sx={{ mt: 0.5, mb: 1, borderRadius: 2, fontSize: 12 }} />}
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                {address || 'Store address'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {hours || 'Opening hours'}
              </Typography>
              {website && (
                <Typography variant="body2" sx={{ color: '#1976d2', mt: 0.5 }}>
                  {website}
                </Typography>
              )}
            </Box>
          </SectionCard>
        </Box>

        {/* Row 2: Media + Brand Story */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>
          {/* Media & Branding */}
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <IconCircle $bgColor="#f3e5f5">
                <ImageIcon sx={{ color: '#9c27b0' }} />
              </IconCircle>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Media & Branding</Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>Your store's visual identity</Typography>
              </Box>
            </Box>

            {/* Logo */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#444', mb: 1.5 }}>Logo</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={logoPreview} alt="Logo" sx={{ width: 80, height: 80, border: '2px solid #e8e8e8' }} />
                <Button variant="outlined" component="label" sx={{ borderRadius: 12, textTransform: 'none', fontWeight: 600 }}>
                  {logo ? 'Change Logo' : 'Upload Logo'}
                  <input type="file" hidden accept="image/*" onChange={(e) => setLogo(e.target.files[0])} />
                </Button>
              </Box>
            </Box>

            {/* Cover */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#444', mb: 1.5 }}>Cover Image</Typography>
              {coverPreview ? (
                <Box component="img" src={coverPreview} alt="Cover" sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 3, mb: 1.5 }} />
              ) : (
                <Box sx={{ width: '100%', height: 140, bgcolor: '#f5f5f5', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, border: '2px dashed #ddd' }}>
                  <Typography variant="body2" color="text.secondary">No cover image</Typography>
                </Box>
              )}
              <Button variant="outlined" component="label" sx={{ borderRadius: 12, textTransform: 'none', fontWeight: 600 }}>
                {cover ? 'Change Cover' : 'Upload Cover'}
                <input type="file" hidden accept="image/*" onChange={(e) => setCover(e.target.files[0])} />
              </Button>
            </Box>
          </SectionCard>

          {/* Brand Story */}
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <IconCircle $bgColor="#fff8e7">
                <AutoAwesomeIcon sx={{ color: '#ffb71b' }} />
              </IconCircle>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Brand Story</Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>Tell customers what makes you special</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <StyledTextField
                label="Tell us about your business and what makes it unique."
                value={brandMeaning}
                onChange={(e) => setBrandMeaning(e.target.value.slice(0, 500))}
                multiline rows={3} fullWidth
                helperText={`${brandMeaning.length}/500`}
              />
              <StyledTextField
                label="How do you want customers to feel when they interact with your business?"
                value={customerFeeling}
                onChange={(e) => setCustomerFeeling(e.target.value.slice(0, 500))}
                multiline rows={3} fullWidth
                helperText={`${customerFeeling.length}/500`}
              />
              <StyledTextField
                label="What are the top 3 reasons customers keep coming back?"
                value={customerReason}
                onChange={(e) => setCustomerReason(e.target.value.slice(0, 500))}
                multiline rows={3} fullWidth
                helperText={`${customerReason.length}/500`}
              />
              <StyledTextField
                label="If a customer could describe your business in one sentence, what would you want them to say?"
                value={customerSaying}
                onChange={(e) => setCustomerSaying(e.target.value.slice(0, 500))}
                multiline rows={3} fullWidth
                helperText={`${customerSaying.length}/500`}
              />
            </Box>
          </SectionCard>
        </Box>

        {/* Row 3: Menu & Items (full width) */}
        <SectionCard sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconCircle $bgColor="#e8f5e9">
              <RestaurantMenuIcon sx={{ color: '#4caf50' }} />
            </IconCircle>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Menu & Items</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>Manage your products and services</Typography>
            </Box>
            <Button
              variant="outlined"
              component="label"
              disabled={menuUploading}
              sx={{ borderRadius: 12, textTransform: 'none', fontWeight: 600 }}
            >
              {menuUploading ? 'Scanning...' : 'Upload Menu'}
              <input type="file" hidden accept="application/pdf,image/*" onChange={handleMenuUpload} />
            </Button>
          </Box>

          {menuUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box component="span" sx={{
                width: 20, height: 20, border: '2px solid #ccc', borderTop: '2px solid #4caf50',
                borderRadius: '50%', display: 'inline-block',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
              }} />
              <Typography variant="body2" color="text.secondary">Scanning menu with AI...</Typography>
            </Box>
          )}

          {menuScanMessage && (
            <Alert
              severity={
                menuScanMessage.startsWith('Added') ? 'success'
                  : /too large|failed|please upload|only image|couldn|error/i.test(menuScanMessage) ? 'error'
                  : 'warning'
              }
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {menuScanMessage}
            </Alert>
          )}

          {items.length > 0 ? (
            <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small" sx={{ minWidth: 440 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#444', borderBottom: '2px solid #e8e8e8' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#444', borderBottom: '2px solid #e8e8e8' }}>Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#444', borderBottom: '2px solid #e8e8e8' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const editing = editingItemId && item._id === editingItemId;
                  return (
                    <TableRow key={item._id || index} sx={{ '&:hover': { bgcolor: editing ? 'transparent' : '#fafafa' } }}>
                      {editing ? (
                        <>
                          <TableCell>
                            <StyledTextField
                              value={editDraft.name}
                              onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                              size="small"
                              fullWidth
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditItem();
                                if (e.key === 'Escape') cancelEditItem();
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <StyledTextField
                              value={editDraft.price}
                              onChange={(e) => setEditDraft((d) => ({ ...d, price: e.target.value }))}
                              size="small"
                              type="number"
                              placeholder="—"
                              inputProps={{ step: 0.01, min: 0 }}
                              sx={{ width: 110 }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditItem();
                                if (e.key === 'Escape') cancelEditItem();
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={saveEditItem} disabled={savingItem} title="Save" sx={{ color: '#4caf50' }}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditItem} disabled={savingItem} title="Cancel" sx={{ color: '#999' }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ color: '#333' }}>{item.name}</TableCell>
                          <TableCell sx={{ color: '#666' }}>
                            {item.price || item.price === 0 ? `$${Number(item.price).toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              onClick={() => startEditItem(item)}
                              title="Edit item"
                              sx={{ color: '#ccc', '&:hover': { color: '#ffb71b' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteItem(index)} title="Delete item" sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center', color: '#999' }}>
              <Typography variant="body2">No items yet. Upload a menu or add items manually.</Typography>
            </Box>
          )}

          {itemError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setItemError('')}>
              {itemError}
            </Alert>
          )}

          {/* Add item */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <StyledTextField label="Item Name" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} sx={{ flex: '2 1 160px' }} size="small" />
            <StyledTextField label="Price" value={newItemPrice} onChange={(e) => setNewItemPrice(e.target.value)} type="number" inputProps={{ step: 0.01, min: 0 }} sx={{ flex: '1 1 100px' }} size="small" />
            <IconButton onClick={handleAddItem} sx={{ bgcolor: '#fff8e7', '&:hover': { bgcolor: '#fff3d0' }, width: 40, height: 40 }}>
              <AddIcon sx={{ color: '#ffb71b' }} />
            </IconButton>
          </Box>
        </SectionCard>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SaveButton onClick={handleSaveProfile} disabled={saving} startIcon={<SaveIcon />}>
            {saving ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        </Box>

        {/* Danger Zone */}
        <SectionCard sx={{ mt: 3, border: '1px solid #ffcdd2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconCircle $bgColor="#ffebee">
              <WarningAmberIcon sx={{ color: '#e53935' }} />
            </IconCircle>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Danger Zone</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>Cancel your account and deactivate your store</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', maxWidth: 560 }}>
              Cancelling your account deactivates your store and signs you out immediately. You won't be able to log back in unless you contact support to reactivate it.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setCancelDialogOpen(true)}
              sx={{ borderRadius: 12, textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
            >
              Cancel Account
            </Button>
          </Box>
        </SectionCard>

        {/* Cancel Account confirmation */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => !cancelling && setCancelDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 460 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {cancelledInfo ? 'Membership cancelled' : 'Cancel your membership?'}
          </DialogTitle>
          <DialogContent>
            {cancelledInfo ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>{cancelledInfo}</Alert>
            ) : (
              <DialogContentText sx={{ color: '#555' }}>
                Your subscription will be cancelled at the end of your current billing period. You'll keep full access until then, after which your account will be deactivated. You can resubscribe anytime.
              </DialogContentText>
            )}
            {cancelError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{cancelError}</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {cancelledInfo ? (
              <Button
                onClick={() => { setCancelDialogOpen(false); setCancelledInfo(''); }}
                variant="contained"
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 10 }}
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setCancelDialogOpen(false)}
                  disabled={cancelling}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#666' }}
                >
                  Keep my membership
                </Button>
                <Button
                  onClick={handleCancelAccount}
                  disabled={cancelling}
                  variant="contained"
                  color="error"
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 10 }}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel membership'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </ContentContainer>
    </PageContainer>
  );
}
